import { useI18n } from "@wojo/localization";
import { Checkbox, ComboBox } from "@wojo/ui";
import React, { useEffect } from "react";
import { useDeparturesStepContext } from "../context/DeparturesStepContext";
import { useClosestAvailableGateway } from "../hooks";

type DeparturePriceWithFlightsProps = {
    /**
     * Automatically set the closest gateway based on geolocation to the quote.
     * If false, the gateway may still be retrieved, but will not be set.
     * @default true
     */
    autoSelectGeolocation?: boolean;
};

export const DeparturePriceWithFlights: React.FC<
    DeparturePriceWithFlightsProps
> = ({ autoSelectGeolocation = true }) => {
    const {
        setFilterStates,
        filterStates,
        withFlightsPriceGateways,
        departures,
    } = useDeparturesStepContext();

    const { productPage } = useI18n("productPage");

    const availableGateways = (withFlightsPriceGateways || []).map(
        (gateway) => ({
            city: gateway.city,
            state: gateway.state,
            code: gateway.code,
            price: departures.hits[0].gatewayPricing?.[gateway.displayName]
                ?.price,
            displayName: gateway.displayName,
        }),
    );

    const { closestRoundtripFlightSummary, isLoading } =
        useClosestAvailableGateway(availableGateways);

    useEffect(() => {
        const noGatewayToSet =
            !closestRoundtripFlightSummary?.displayName &&
            !availableGateways.length;
        if (
            filterStates.priceWithFlightsGateway ||
            !autoSelectGeolocation ||
            isLoading ||
            noGatewayToSet
        ) {
            return;
        }

        setFilterStates({
            priceWithFlightsGateway:
                closestRoundtripFlightSummary?.displayName ??
                availableGateways[0]?.displayName,
        });
    }, [
        closestRoundtripFlightSummary?.displayName,
        availableGateways,
        autoSelectGeolocation,
        filterStates.priceWithFlightsGateway,
        isLoading,
        setFilterStates,
    ]);

    if (!availableGateways.length) {
        return null;
    }

    return (
        <Checkbox
            label={productPage.tripBuilder.filters.gatewaySelectorToggleLabel.l()}
            checked={filterStates.priceWithFlights ?? false}
            onChange={(event) => {
                setFilterStates({
                    priceWithFlights: event.target.checked,
                });
            }}
            moreDetails={
                filterStates.priceWithFlights && (
                    <ComboBox
                        items={availableGateways.map((gateway) => ({
                            value: gateway.displayName,
                            label: gateway.displayName,
                        }))}
                        value={filterStates.priceWithFlightsGateway}
                        placeholder={productPage.tripBuilder.filters.gatewaySelectorPlaceholder.l()}
                        label={productPage.tripBuilder.filters.gatewaySelectorLabel.l()}
                        fieldId="priceWithFlightsSelector"
                        onChange={(value) => {
                            setFilterStates({
                                priceWithFlightsGateway: value,
                            });
                        }}
                    />
                )
            }
        />
    );
};
